/* SØLVSTONE: free-gift progress bar, currency-aware offer amounts, giveaway popup */
(() => {
  const S = () => window.Shopify || {};
  const root = () => (S().routes && S().routes.root) || '/';
  const rate = () => parseFloat((S().currency && S().currency.rate) || '1') || 1;
  const cur = () => (S().currency && S().currency.active) || 'EUR';
  const lang = () => document.documentElement.lang || 'en';

  /** Format an amount in major units of the active currency, no decimals. */
  const fmtUnits = (units, currency) =>
    new Intl.NumberFormat(lang(), {
      style: 'currency',
      currency: currency || cur(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(units);
  const fmtCents = (cents, currency) => fmtUnits(Math.ceil(cents / 100), currency);
  const eurToLocal = (eur, up) => (up ? Math.ceil(eur * rate()) : Math.round(eur * rate()));

  /* ---------- Convert "€400"-style amounts for non-EUR visitors ---------- */
  function localiseAmounts(scope) {
    const el = scope || document;
    el.querySelectorAll('[data-sv-eur]').forEach((node) => {
      const eur = parseFloat(node.getAttribute('data-sv-eur'));
      if (!isNaN(eur)) node.textContent = fmtUnits(eurToLocal(eur));
    });
    if (cur() === 'EUR') return;
    // Announcement bar text cannot carry attributes, so swap literal "€400" there.
    el.querySelectorAll('header-announcements, .announcement-bar, [class*="announcement"]').forEach((bar) => {
      const walker = document.createTreeWalker(bar, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = walker.nextNode())) {
        if (n.nodeValue.includes('€400')) n.nodeValue = n.nodeValue.replace(/€400/g, fmtUnits(eurToLocal(400, true)));
      }
    });
  }
  document.addEventListener('DOMContentLoaded', () => localiseAmounts());
  document.addEventListener('shopify:section:load', (e) => localiseAmounts(e.target));

  /* ---------- Free gift progress bar (cart drawer + cart page) ---------- */
  let giftCache = null;
  async function getGift(handle) {
    if (giftCache) return giftCache;
    try {
      const r = await fetch(`${root()}products/${handle}.js`, { credentials: 'same-origin' });
      if (!r.ok) return null;
      giftCache = await r.json();
      return giftCache;
    } catch (_) {
      return null;
    }
  }
  const getCart = () => fetch(`${root()}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
  const post = (path, body) =>
    fetch(`${root()}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'sv-gift-bar', data: { itemCount: cart.item_count, source: 'sv-gift-bar' } },
      })
    );
  }

  const CSS = `
    :host{display:block;margin:0 0 16px}
    .box{background:#f4f5f6;border-radius:8px;padding:12px 14px;font-size:13px;line-height:1.45;color:#111}
    .msg{margin:0 0 8px}
    .track{height:6px;background:#dfe3e7;border-radius:99px;overflow:hidden}
    .fill{height:100%;background:#111;border-radius:99px;transition:width .6s ease}
    .sub{margin:7px 0 0;color:#6b7280;font-size:12px}
    .row{display:flex;gap:8px;margin-top:10px;flex-wrap:wrap}
    select{flex:1 1 0;min-width:0;font:inherit;font-size:13px;padding:8px 10px;border:1px solid #cfd4d9;border-radius:6px;background:#fff;color:#111}
    button{flex:1 1 100%;font:inherit;font-size:13px;font-weight:600;padding:11px;border:0;border-radius:6px;background:#111;color:#fff;cursor:pointer}
    button[disabled]{opacity:.55;cursor:default}
    .ok{display:flex;gap:8px;align-items:center;margin:0 0 8px}
    .ok svg{flex:none}
  `;
  const GIFT_ICON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="3" y="8" width="18" height="13" rx="1.5"/><path d="M12 8v13M3 12h18M12 8S10.5 3 7.5 3.8C5 4.5 6.5 8 12 8Zm0 0s1.5-5 4.5-4.2C19 4.5 17.5 8 12 8Z"/></svg>';
  const CHECK_ICON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  class SvGiftBar extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
      this._onUpdate = (e) => {
        if (e && e.detail && e.detail.data && e.detail.data.source === 'sv-gift-bar') return;
        this.refresh();
      };
      document.addEventListener('cart:update', this._onUpdate);
      this.refresh();
    }
    disconnectedCallback() {
      document.removeEventListener('cart:update', this._onUpdate);
    }
    refresh() {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.run(), 200);
    }
    async run() {
      const handle = this.dataset.handle;
      const thresholdEur = parseFloat(this.dataset.threshold || '400');
      const [cart, gift] = await Promise.all([getCart(), getGift(handle)]);
      if (!gift) return this.paint('');

      const thresholdCents = eurToLocal(thresholdEur, true) * 100;
      const giftLines = cart.items.filter((i) => i.handle === handle);
      const eligible = cart.items.filter((i) => i.handle !== handle).reduce((s, i) => s + i.final_line_price, 0);
      const giftQty = giftLines.reduce((s, i) => s + i.quantity, 0);

      // Keep the cart honest: gift only when eligible, and never more than one.
      if (giftLines.length && (eligible < thresholdCents || giftQty > 1)) {
        const updates = {};
        giftLines.forEach((l, idx) => (updates[l.key] = eligible >= thresholdCents && idx === 0 ? 1 : 0));
        await post('cart/update.js', { updates });
        const fresh = await getCart();
        announce(fresh);
        return this.run();
      }
      if (eligible <= 0) return this.paint('');

      const minPrice = Math.min(...gift.variants.map((v) => v.price));
      const worth = fmtCents(minPrice, cart.currency);

      if (giftLines.length) {
        return this.paint(`<div class="box"><p class="ok">${CHECK_ICON}<span>Your <b>free tennis bracelet</b> is in your bag.</span></p>
          <div class="track"><div class="fill" style="width:100%"></div></div></div>`);
      }

      if (eligible < thresholdCents) {
        const pct = Math.max(4, Math.min(100, (eligible / thresholdCents) * 100));
        return this.paint(`<div class="box"><p class="msg">You're <b>${fmtCents(thresholdCents - eligible, cart.currency)}</b> away from a <b>free tennis bracelet</b></p>
          <div class="track"><div class="fill" style="width:${pct.toFixed(1)}%"></div></div>
          <p class="sub">Worth ${worth}. 925 silver and lab-grown moissanite.</p></div>`);
      }

      // Unlocked: let the buyer pick finish and length.
      const opt = (i) => [...new Set(gift.variants.map((v) => v.options[i]))];
      const optName = (i, fallback) => {
        const o = (gift.options || [])[i];
        return (o && (typeof o === 'string' ? o : o.name)) || fallback;
      };
      const esc = (s) => String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;');
      const sel = (name, i) =>
        `<select aria-label="${esc(name)}">${opt(i).map((o) => `<option value="${esc(o)}">${esc(o)}</option>`).join('')}</select>`;
      this.paint(`<div class="box"><p class="ok">${GIFT_ICON}<span>You've unlocked a <b>free tennis bracelet</b> (worth ${worth}).</span></p>
        <div class="track"><div class="fill" style="width:100%"></div></div>
        <div class="row">${sel(optName(0, 'Finish'), 0)}${sel(optName(1, 'Length'), 1)}
        <button type="button">Add free bracelet</button></div></div>`);

      const btn = this.shadowRoot.querySelector('button');
      btn.addEventListener('click', async () => {
        const picks = [...this.shadowRoot.querySelectorAll('select')].map((s) => s.value);
        const v = gift.variants.find((x) => x.options[0] === picks[0] && x.options[1] === picks[1]) || gift.variants[0];
        btn.disabled = true;
        btn.textContent = 'Adding…';
        await post('cart/add.js', { items: [{ id: v.id, quantity: 1 }] });
        const fresh = await getCart();
        announce(fresh);
        this.run();
      });
    }
    paint(html) {
      this.shadowRoot.innerHTML = html ? `<style>${CSS}</style>${html}` : '';
    }
  }
  if (!customElements.get('sv-gift-bar')) customElements.define('sv-gift-bar', SvGiftBar);

  /* ---------- Giveaway popup ---------- */
  const KEY = 'sv_popup_v1';
  const load = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '{}');
    } catch (_) {
      return {};
    }
  };
  const save = (o) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(Object.assign(load(), o)));
    } catch (_) {}
  };

  class SvPopup extends HTMLElement {
    connectedCallback() {
      localiseAmounts(this);
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this._esc = (e) => e.key === 'Escape' && !this.hidden && this.close();
      document.addEventListener('keydown', this._esc);
      const form = this.closest('form') || this.querySelector('form');
      if (form) form.addEventListener('submit', () => save({ done: Date.now() }));

      if (this.dataset.openNow) {
        // Shopify marks every customer form as posted; only react to our own form.
        if (location.hash !== '#sv-popup-form') {
          if (this.dataset.success) save({ done: Date.now() });
          return;
        }
        this.open(false);
        if (this.dataset.success) save({ done: Date.now() });
        return;
      }
      if (S().designMode) return; // shown via section select in the editor
      const st = load();
      if (st.done) return;
      if (st.closed && Date.now() - st.closed < 7 * 864e5) return;

      let fired = false;
      const fire = () => {
        if (fired) return;
        fired = true;
        cleanup();
        this.open(true);
      };
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - innerHeight;
        if (max > 0 && scrollY > max * 0.5) fire();
      };
      const onLeave = (e) => {
        if (e.clientY <= 0 && !e.relatedTarget) fire();
      };
      const timer = setTimeout(fire, (parseFloat(this.dataset.delay) || 8) * 1000);
      const cleanup = () => {
        clearTimeout(timer);
        removeEventListener('scroll', onScroll);
        document.removeEventListener('mouseout', onLeave);
      };
      addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('mouseout', onLeave);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this._esc);
    }
    open(focus) {
      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      document.documentElement.classList.add('svp-lock');
      const touch = matchMedia('(hover: none)').matches;
      if (focus && !touch) setTimeout(() => this.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 350);
    }
    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('svp-lock');
      setTimeout(() => (this.hidden = true), 250);
      if (!load().done) save({ closed: Date.now() });
    }
  }
  if (!customElements.get('sv-popup')) customElements.define('sv-popup', SvPopup);

  // Theme editor: open the popup while its section is selected.
  document.addEventListener('shopify:section:select', (e) => e.target.querySelector('sv-popup')?.open(false));
  document.addEventListener('shopify:section:deselect', (e) => e.target.querySelector('sv-popup')?.close());
})();
